// React
import React from 'react';

import { css } from '@emotion/core';
import CybsTooltip from '@cybs-ui/components/lib/tooltip';
import shortid from 'shortid';

import CybsScreenReaderManager from '@cybs-ui/components/lib/screen-reader-manager';

// helpers
import defaultGridProps from './utils/defaultProps';
import { placeListboxInTable } from '../../utils/menuAlignment';

// Components
import { GridComponents } from './components';
import BaseGridContainers from './containers/BaseTable';
import BaseTableComponent from './components/BaseTableComponent';
import DefaultEmptyView from './components/DefaultEmptyView';

import baseStyles from './styles/base.styles';

const DISPLAY_NAME = 'BaseTable';
export default class BaseTable extends BaseTableComponent {
  static displayName = DISPLAY_NAME;

  static defaultProps = defaultGridProps;

  constructor(props) {
    super({
      ...props,
      childDisplayName: DISPLAY_NAME
    });
    this.state = {
      focused: {
        columnIdx: 0,
        rowIdx: 1
      },
      showTooltip: false
    };

    this.i18n = {
      rowActionConfig: {
        rowSelection: {
          ariaLabel: 'select all rows',
          headerLabel: 'select'
        }
      }
    };
     this.selectallId = `select-all-rows-${shortid.generate()}`;
  }

  onFocus = (rowIdx, columnIdx) => {
    this.setState({
      focused: {
        columnIdx,
        rowIdx
      }
    });
  };

  componentDidMount = () => {
    this.forceUpdate();
  };

  handleMouseEnter = () => {
    this.setState({ showTooltip: true });
  };

  handleMouseLeave = () => {
    this.setState({ showTooltip: false });
  };

  isFocused = (rowIdx, colIdx) => {
    const focusedRowIdx = this.state.focused.rowIdx;
    const focusedColIdx = this.state.focused.columnIdx;

    return focusedRowIdx === rowIdx && focusedColIdx === colIdx;
  };

  renderRowDataCell = (cell, cellIdx, rowIdx) => {
    const { renderCell, onCellChange } = this.props;
    const colOffset = this.columnIndexOffset;

    const handleFocus = () => this.onFocus(rowIdx + 1, cellIdx);
    const body = cell.hide
      ? null
      : renderCell(cell, cellIdx, rowIdx, onCellChange);
    const tdProps = this.tdProps(rowIdx, cellIdx + colOffset);

    return (
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
      <td {...tdProps} onFocus={handleFocus}>
        {body}
      </td>
    );
  };

  getHeaderId = headerIdx => {
    const { headers, resolvedHeaders, variant } = this.props;
    const tHeaders = variant !== this.types.BASE ? resolvedHeaders : headers;
    const headerId =
      tHeaders && tHeaders[headerIdx] && tHeaders[headerIdx].id
        ? tHeaders[headerIdx].id
        : null;

    return headerId;
  };

  tdProps = (rowIdx, cellIdx) => {
    const { dataTest, getTdProps, variant, tableId } = this.props;

    if (variant === this.types.DISPLAY) {
      // initialize virtual idx and add it
      const virtualIndex = this.props.rows.findIndex(({ id }) => id === rowIdx);
      if (virtualIndex !== -1) rowIdx = virtualIndex;
    }

    const baseTdProps = {
      className: 'paddingCell',
      'data-row': rowIdx + 1,
      'data-col': cellIdx + 1,
      'data-parent': tableId,
      'data-test': `${this.getHeaderId(cellIdx)}-tableCell-${
        rowIdx + 1
      }-${dataTest}-cybsTable`,
       'key': `${tableId}-${rowIdx}`
    };

    let inheritedProps = {};
    if (getTdProps && typeof getTdProps === 'function') {
      inheritedProps = getTdProps(rowIdx, cellIdx);
    }

    return {
      ...baseTdProps,
      ...inheritedProps
    };
  };

  renderHeaderIcon = (headerId, headerIdx, iconProps) => {
    const { sortName, sortedLabelId, sortLabel } = iconProps;
    const { dataTest, onSort, tableId } = this.props;
    const sortInfo = {
      headerId,
      headerIdx
    };
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <GridComponents.Button
        id={`${tableId}-${sortedLabelId}`}
        dataTest={`${dataTest}-${sortedLabelId}`}
        i18n={{ ariaLabel: sortLabel }}
        variant="icon"
        iconClass={sortName}
        resolution="tiny"
        iconType="light-tiny"
        onClick={() => onSort(sortInfo)}
      />
    );
  };

  /**
   * Renders header along with aria live region for sort updates
   */
  // eslint-disable-next-line no-unused-vars
  renderHeaderCell = (columnHeader, headerIdx, rowIdx) => {
    const { dataTest, tableId, sortColumn } = this.props;
    const sortColumnIdx =
      sortColumn && sortColumn.index > -1 ? sortColumn.index : -1;
    const sortType = headerIdx === sortColumnIdx ? sortColumn.type : 'sortable';
    const isSortable = columnHeader.sortable;
    const sortName = sortType || 'sortable';

    // Event Handlers
    const handleFocus = () => this.onFocus(0, headerIdx);
    const offset = this.columnIndexOffset;
    const thProps = this.thProps(headerIdx + offset);
    const sortedLabelId = `sortIconLabel-${columnHeader.id}`;
    const liveId = `${columnHeader.id}-screenreaderarea`;
    const sortLabels = {
      // @TODO i18n
      sortable: `${columnHeader.label} Sortable`,
      'sortable-descending': `${columnHeader.label} Sorted Descending`,
      'sortable-ascending': `${columnHeader.label} Sorted Ascending`
    };

    const sortLabel = sortLabels[sortName];
    const iconProps = {
      sortLabel,
      sortedLabelId,
      sortName
    };

    const headerId =
      columnHeader && columnHeader.id ? columnHeader.id : headerIdx;

    const _renderBody = () => {
      const children = isSortable ? (
        <React.Fragment>
          <span css={baseStyles.headerText} aria-hidden="true">{columnHeader.label}</span>
          {this.renderHeaderIcon(headerId, headerIdx, iconProps)}
        </React.Fragment>
      ) : (
        <span css={baseStyles.headerText}>{columnHeader.label}</span>
      );
      const alignDictionary = {
        right: 'row-reverse'
      };
      return (
        <div
          style={{
            flexDirection:
              columnHeader.align &&
              Object.keys(alignDictionary).indexOf(columnHeader.align) > -1
                ? alignDictionary[columnHeader.align]
                : 'inherit',
            width: '100%',
            height: '100%',
            display: 'inline-flex',
            alignItems: 'center'
          }}
        >
          {children}
        </div>
      );
    };

    return (
      <th
        id={this.getHeaderId(headerIdx + offset)}
        key={`table-header-${headerIdx}`}
        data-row={1}
        data-col={headerIdx + offset + 1}
        data-parent={tableId}
        data-test={`${this.getHeaderId(headerIdx)}-${dataTest}-columnHeader-cybsTable`}
        onFocus={handleFocus}
        data-innerfocus
        scope="col"
        {...thProps}
      >
        {_renderBody()}
        {isSortable ? (
          <div
            aria-atomic="false"
            aria-live="polite"
            style={{ display: 'none' }}
            id={liveId}
          />
        ) : null}
      </th>
    );
  };

  get columnIndexOffset() {
    const { rowActionConfig } = this.props;

    return rowActionConfig && Array.isArray(rowActionConfig)
      ? rowActionConfig.length
      : 0;
  }

  getRowActionWidthCSS = rowAction =>
    // const flagWidth
    rowAction && rowAction.type
      ? css`
          min-width: ${this.getRowActionWidth(rowAction)}px;
          width: ${this.getRowActionWidth(rowAction)}px;
        `
      : null;

  thProps = headerIdx => {
    const thProps = {};
    let inheritedProps;
    const { getThProps } = this.props;

    if (getThProps && typeof getThProps === 'function') {
      inheritedProps = getThProps(headerIdx);
    }

    return {
      ...inheritedProps,
      ...thProps
    };
  };

  tfProps = headerIdx => {
    const { isRowActionIdx, rowActionConfig } = this.props;

    const isRowAction = isRowActionIdx(headerIdx);
    if (isRowAction) {
      const rowAction =
        rowActionConfig && rowActionConfig.length > 0
          ? rowActionConfig[headerIdx]
          : null;
      return {
        css: this.getRowActionWidthCSS(rowAction)
      };
    }

    return {};
  };

  renderTableItem = (tableEl, cell, cellIdx, rowIdx) => {
    const { renderTableFilter } = this.props;
    const renderDictionary = {
      th: this.renderHeaderCell,
      td: this.renderRowDataCell,
      tf: renderTableFilter
    };

    const renderer =
      tableEl && renderDictionary[tableEl] ? renderDictionary[tableEl] : null;
    if (renderer) return renderer(cell, cellIdx, rowIdx);

    return null;
  };

  /**
   * Header row group props
   */
  theadProps = () => {
    const { getTheadProps } = this.props;
    const theadProps = {};

    let inheritedProps = {};

    if (getTheadProps && typeof getTheadProps === 'function') {
      inheritedProps = getTheadProps();
    }

    return { ...theadProps, ...inheritedProps };
  };

  trProps = () => {
    let trProps = {};
    let inheritedProps = {};
    const { getTrProps } = this.props;

    if (getTrProps && typeof getTrProps === 'function') {
      inheritedProps = getTrProps();

      trProps = inheritedProps;
    }
    return { ...trProps, ...inheritedProps };
  };

  /**
   * These are standard flags for row actions. Can be extended in the future to support more standard row actions e.g. delete.
   */
  renderRowActionFlag = (type, rowAction, rowIdx, cellIdx, rowHeader) => {
    const aType = rowAction && rowAction.type ? rowAction.type : null;
    switch (aType) {
      case 'rowSelection':
        return this.renderRowSelection(type, rowAction, rowIdx, cellIdx, rowHeader);
      default:
        return <div />;
    }
  };

  handleRowHeaderKeyDown = e => {
    if (e.key === 'Escape' || e.keyCode === 27) {
      e.nativeEvent.stopImmediatePropagation();
      this.setState({ showTooltip: false });
    }
  };

  renderRowSelection = (type, rowAction, rowIdx, cellIdx, rowHeader) => {
    const { dataTest, rows, tooltipShowOn, tableId } = this.props;
    const { showTooltip } = this.state;
    const { options } = rowAction;
    const disabled = rowAction && rowAction.disabled ? rowAction.disabled : [];
    const { i18n } = options;
    const defaultLocales = this.i18n.rowActionConfig.rowSelection;
    const headerLabel =
      i18n && i18n.headerLabel ? i18n.headerLabel : defaultLocales.headerLabel;
    const _handleRowHeader = () => {
      const {
        toggleSelectAll,
        selected,
        getSelectAll,
        isSelectedInPage
      } = this.props;
      const isSelectAll = getSelectAll();
      const isRowSelectedInPage = isSelectedInPage();
      return (
        <>
          <CybsTooltip
            dataTest={`${dataTest}-select-all-tooltip`}
            label={i18n.selectAllLabel}
            showOn={tooltipShowOn}
            isShown={showTooltip}
          >
            <div
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
            >
              <GridComponents.Checkbox
                aria-label={headerLabel}
                dataTest={`${dataTest}-select-all-checkbox`}
                disabled={!(rows && rows.length > 0)}
                indeterminate={
                  selected &&
                  selected.length &&
                  !isSelectAll &&
                  isRowSelectedInPage
                }
                data-innerfocus
                id={this.selectallId}
                name="select-all-rows"
                colIdx={cellIdx}
                rowIdx={rowIdx}
                checked={isSelectAll}
                onCheck={() => toggleSelectAll()}
                aria-describedby="select-all-rows-label"
                onKeyDown={this.handleRowHeaderKeyDown}
                onFocus={this.handleMouseEnter}
                onBlur={this.handleMouseLeave}
              />
              <span aria-hidden="true" style={{ display: 'none' }} id="select-all-rows-label">{i18n.selectAllLabel}</span>
            </div>
          </CybsTooltip>
        </>
      );
    };
    const _handleRowCell = () => {
      const { isSelected, selectRow } = this.props;
      const data = this.props.wrappedState.displayedData;
      const idx = (rows && rows?.length) ? data.findIndex(item => item.id.toString() === rowIdx.toString()) : rowIdx;
      return (
        <GridComponents.Checkbox
          data-innerfocus
          id={`${tableId}-select-row-${rowIdx}`}
          disabled={disabled && disabled.length > 0 && disabled.indexOf(rowIdx) > -1}
          dataTest={`${dataTest}-select-row-${rowIdx}`}
          colIdx={cellIdx}
          rowIdx={rowIdx}
          checked={isSelected(rowIdx)}
          onCheck={() => selectRow(rowIdx)}
          i18n={{
            ariaLabel:
              (rowHeader && `${headerLabel} ${rowHeader}`) ||
              i18n.checkAriaLabel ||
              `${headerLabel} entry ${parseInt(idx, 10) + 1}`
          }}
        />
      );
    };

    const dic = {
      th: _handleRowHeader,
      td: _handleRowCell
    };

    const renderer = type && dic[type] ? dic[type] : null;

    if (renderer) {
      return renderer();
    }

    return null;
  };

  isValidRowActionFlag = type => this.rowActionFlags.includes(type);

  renderRowActionHeader = (rowAction, rowIdx, cellIdx) => {
    if (this.isValidRowActionFlag(rowAction.type)) {
      return this.renderRowActionFlag('th', rowAction, rowIdx, cellIdx);
    }
    return rowAction.label;
  };

  renderRowActionBody = (tableEl, rowAction, rowIdx, cellIdx, rowHeader) => {
    // passing table element to render correct element for table whether it be th or td
    const rowActionRendererDictionary = {
      th: this.renderRowActionHeader,
      td: this.renderRowActionItem
    };

    const renderer = rowActionRendererDictionary[tableEl];

    return renderer ? renderer(rowAction, rowIdx, cellIdx, rowHeader) : <div />;
  };

  getRowActionWidth = rowAction => {
    if (rowAction && rowAction.width) {
      return rowAction.width; // short circuit for edge cases
    }

    let bodyWidth = 6.5 * this.getDocumentRemNumberValue();
    const refKey = this.getRefKey(rowAction.type);
    if (
      rowAction &&
      rowAction.type &&
      this.isValidRowActionFlag(rowAction.type) &&
      this[refKey]
    ) {
      return this[refKey].offsetWidth;
    }
    if (
      rowAction &&
      rowAction.options &&
      rowAction.options.variant === this.BUTTON_TYPES.TEXT
    ) {
      bodyWidth = 10 * this.getDocumentRemNumberValue();
    }
    return bodyWidth;
  };

  renderRowActionItem = (rowAction, rowIdx, cellIdx, rowHeader) => {
    const { type } = rowAction;
    const { dataTest, onCellChange, rows, tableId, stickyHeaders } = this.props;
    const i18nProps = {
      row: 'row',
      ...rowAction?.options?.i18n
    };
    const rest = rowAction && rowAction.options ? rowAction.options : {};
    const disabled = rowAction && rowAction.disabled ? rowAction.disabled : [];
    const data = this.props.wrappedState.displayedData;
    const idx = (rows && rows?.length) ? data.findIndex(item => item.id.toString() === rowIdx.toString()) : rowIdx;
    if (this.isValidRowActionFlag(type)) {
      return this.renderRowActionFlag('td', rowAction, rowIdx, cellIdx, rowHeader);
    }
    const handleChange = (e, selected) => {
      onCellChange(selected, rowAction, rowIdx, cellIdx, e);
    };
    const rowActionComponentDictionary = {
      menu: GridComponents.IconMenu,
      default: GridComponents.Button
    };
    const Component = Object.keys(rowActionComponentDictionary).includes(
      rowAction.type
    )
      ? rowActionComponentDictionary[rowAction.type]
      : rowActionComponentDictionary.default;
    const actionHeader = rowHeader ? `${i18nProps?.label} ${rowHeader}` : `${i18nProps?.label} ${i18nProps?.row} ${idx + 1}`;
    const componentProps = {
      disabled:
        disabled && disabled.length > 0 && disabled.indexOf(rowIdx) > -1,
      colorScheme: 'secondary',
      iconType: 'light',
      [rowAction.type === 'menu' ? 'onSelect' : 'onClick']: handleChange,
      ...rest,
      i18n: {
        ariaLabel: actionHeader,
        ...i18nProps,
        label: (rowAction?.options?.variant === 'icon') ? actionHeader : i18nProps?.label
      },
      dataTest:
        rowAction?.dataTest && `${dataTest}-${rowAction.dataTest}-${rowIdx}`,
      // Passing tableId for iconmenu row action listbox placement when not sticky
      tableId: stickyHeaders ? '' : tableId
    };
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Component {...componentProps} />
      </div>
    );
  };

  setRef = (r, id) => {
    const key = this.getRefKey(id);
    let changed = false;
    if (r) {
      const curr = this[key];
      if (!curr || curr.offsetWidth !== r.offsetWidth) changed = true;
      this[key] = r;
    }
    if (changed) {
      this.setState({ didUpdateForRef: changed }); // assigning new ref doesn't update react...
    }
  };

  /**
   * Used to set ref for header in context
   */
  getRefKey = id => `${id}Ref`;

  tbodyProps = () => {
    let tbodyProps = {};
    let inheritedProps = {};
    const { getTbodyProps } = this.props;

    if (getTbodyProps && typeof getTbodyProps === 'function') {
      inheritedProps = getTbodyProps();

      tbodyProps = inheritedProps;
    }
    return { ...tbodyProps, ...inheritedProps };
  };

  shouldRenderEmptyFilterView = () => {
    const { rows, filters } = this.props;
    if (
      rows &&
      rows.length === 0 &&
      filters &&
      Array.isArray(filters) &&
      filters.length > 0
    ) {
      return true;
    }
    return false;
  };

  getTableWrapperStyle = () => {
    const baseWrapper =
      baseStyles && Object.keys(baseStyles).indexOf('tableWrapper') !== -1
        ? baseStyles.tableWrapper({ minWidth: this.props.minWidth })
        : null;
    let inheritedWrapper;
    const { styles } = this.props;

    if (styles && Object.keys(styles).indexOf('tableWrapper') !== -1) {
      inheritedWrapper = styles.tableWrapper;
    }
    return inheritedWrapper || baseWrapper;
  };

  paginationProps = () => {
    const { headers } = this.props;
    return {
      css: theme => baseStyles.minTableWidth(headers, theme)
    };
  };

  get tableColumnLength() {
    const { headers, rowActionConfig } = this.props;
    let val = headers.length > 0 ? headers.length : 0;

    if (rowActionConfig && this.isNonEmptyArray(rowActionConfig)) {
      val += this.columnIndexOffset;
    }

    return val;
  }

  hasRowActions = () => {
    const { variant } = this.props;

    return variant !== this.types.BASE;
  };

  render() {
    const {
      allRowsLength,
      caption,
      dataTest,
      emptyViewFilter,
      filters,
      tableId,
      headers,
      i18n: i18nUser,
      isControlledPagination,
      onPageChange,
      onFooterClick,
      hidePagination,
      page,
      pageSize,
      pagesToShow,
      rows,
      rowActionConfig,
      sortColumn,
      styles,
      totalPages,
      variant,
      leftFooter,
      focusable,
      selectRow,
      selectOnRowClick,
      stickyHeaders,
      stickyHeight,
      hidePadRows,
      shouldRenderEmptyFilterView,
      jumpToLast
    } = this.props;

    const i18n = {
      ...defaultGridProps.i18n,
      ...i18nUser
    };
    const hasRowActions = this.hasRowActions();
    const baseGridContainerProps = {
      dataTest,
      renderTableItem: this.renderTableItem,
      rowActionConfig,
      headers,
      variant,
      hasRowActions,
      tableId
    };

    const tableClassName = `${stickyHeaders ? 'sticky' : ''} ${selectOnRowClick ? 'first-sticky' : ''}`;
    const isEmptyTable = shouldRenderEmptyFilterView || this.shouldRenderEmptyFilterView();

    if (isEmptyTable) {
      CybsScreenReaderManager.addNotification(`${i18n?.title} ${i18n?.subtitle}`);
    }

    return (
      <div style={{ position: 'relative' }}>
        <div
          tabIndex="-1"
          style={{
            overflow: variant === 'display' ? 'auto' : undefined,
            maxHeight: stickyHeaders ? stickyHeight ?? '32rem' : undefined,
            overflowX: variant !== 'display' ? 'auto' : undefined
          }}
          onScroll={() => {
            if (!stickyHeaders) {
              placeListboxInTable(tableId);
            }
          }}
        >
        <table // eslint-disable-line
          css={this.getTableWrapperStyle()}
          data-test={`${dataTest}-cybsTable`}
          id={tableId}
          tabIndex={focusable ? 0 : undefined} // eslint-disable-line
          className={tableClassName}
        >
          <caption className="cybs-a11y-hidden">{caption}</caption>
          <BaseGridContainers.Header
            selectRow={selectOnRowClick}
            theadProps={this.theadProps}
            tfProps={this.tfProps}
            thProps={this.thProps}
            columnIndexOffset={this.columnIndexOffset}
            filters={filters}
            isValidRowActionFlag={this.isValidRowActionFlag}
            getRowActionWidthCSS={this.getRowActionWidthCSS}
            renderRowActionBody={this.renderRowActionBody}
            sortColumn={sortColumn}
            setRef={this.setRef}
            {...baseGridContainerProps}
          />
          <BaseGridContainers.Body
            selectRow={selectOnRowClick ? selectRow : undefined}
            pageSize={pageSize}
            tdProps={this.tdProps}
            trProps={this.trProps}
            tBodyProps={this.tbodyProps}
            i18n={i18n}
            numColumns={this.tableColumnLength}
            rows={rows}
            isFocused={this.isFocused}
            getRowActionWidth={this.getRowActionWidth}
            onFocus={this.onFocus}
            renderRowActionBody={this.renderRowActionBody}
            isValidRowActionFlag={this.isValidRowActionFlag}
            getRowActionWidthCSS={this.getRowActionWidthCSS}
            shouldRenderEmptyFilterView={isEmptyTable}
            hidePadRows={hidePadRows}
            {...baseGridContainerProps}
          />
        </table>
        </div>
        {
          isEmptyTable && (
            <div
              style={{
                height: '250px',
                position: 'absolute',
                marginTop: '-250px',
                width: 'calc(100% - 2rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              {emptyViewFilter || <DefaultEmptyView i18n={{ title: i18n?.title, subtitle: i18n?.subtitle }} />}
            </div>
          )}
        <BaseGridContainers.Footer
          leftFooter={leftFooter}
          styles={styles}
          page={page}
          pagesToShow={pagesToShow}
          totalPages={totalPages}
          allRowsLength={allRowsLength}
          pageSize={pageSize}
          hidePagination={hidePagination}
          onPageChange={onPageChange}
          onFooterClick={onFooterClick}
          i18n={i18n}
          jumpToLast={jumpToLast}
          isControlledPagination={isControlledPagination}
          {...baseGridContainerProps}
        />
      </div>
    );
  }
}
